import { watch, unref } from 'vue';
import { localeMappings, supportedLocales } from '~/i18n/config';
import type { SupportedLocale } from '~/i18n/config';

export default defineNuxtPlugin({
  name: 'musicmint-locale-client',
  dependsOn: ['i18n:plugin', 'i18n:plugin:route-locale-detect'],
  async setup(nuxtApp) {
    const i18n = nuxtApp.$i18n;
    const renderedLocale = useState<SupportedLocale | null>('musicmint-rendered-locale', () => null);
    // Hydrate in the server's language before reconciling browser-only preferences.
    if (renderedLocale.value) await i18n.setLocale(renderedLocale.value);

    const browserLocale = (): SupportedLocale => {
      const language = navigator.language.toLowerCase();
      const mapped = localeMappings.browserToApp[language as keyof typeof localeMappings.browserToApp];
      const primary = language.split('-')[0] as SupportedLocale;
      return mapped || (supportedLocales.includes(primary) ? primary : 'en');
    };
    const initialLocale = (): SupportedLocale => {
      try {
        const saved = localStorage.getItem('locale');
        if (supportedLocales.includes(saved as SupportedLocale)) return saved as SupportedLocale;
        const mapped = localeMappings.fullToShort[saved as keyof typeof localeMappings.fullToShort];
        return mapped || browserLocale();
      } catch { return 'en'; }
    };
    const persistLocale = (locale: SupportedLocale) => {
      try { localStorage.setItem('locale', locale); } catch { /* Language selection still works without storage. */ }
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      document.cookie = `locale=${locale}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
      i18n.setLocaleCookie(locale);
    };

    let revision = 0;
    let desiredLocale: SupportedLocale | null = null;
    let pending = Promise.resolve();
    let releaseHydration: () => void;
    const hydrationReady = new Promise<void>(resolve => { releaseHydration = resolve; });
    // Keep the existing synchronous boolean API; serialize asynchronous module switches.
    const changeLanguage = (locale: string) => {
      if (!supportedLocales.includes(locale as SupportedLocale)) return false;
      const selected = locale as SupportedLocale;
      const request = ++revision;
      desiredLocale = selected;
      pending = pending.then(async () => {
        await hydrationReady;
        if (request !== revision) return;
        await i18n.setLocale(selected);
        if (request !== revision) return;
        persistLocale(selected);
        desiredLocale = null;
        window.dispatchEvent(new CustomEvent('locale-changed', { detail: selected }));
      }).catch(error => { console.error('Language selection failed:', error); });
      return true;
    };

    let mounted = false;
    let initialized = false;
    const finishHydration = async () => {
      if (initialized || !mounted || nuxtApp.isHydrating) return;
      initialized = true;
      watch(() => unref(i18n.locale), locale => {
        if (supportedLocales.includes(locale as SupportedLocale) && (!desiredLocale || desiredLocale === locale)) {
          persistLocale(locale as SupportedLocale);
        }
      }, { flush: 'sync' });
      releaseHydration!();
      // An explicit choice made while a page hydrates wins over startup defaults.
      if (!desiredLocale) changeLanguage(initialLocale());
      await pending;
      window.addEventListener('languagechange', () => {
        const detected = browserLocale();
        if (detected !== unref(i18n.locale)) changeLanguage(detected);
      });
    };
    nuxtApp.hook('app:mounted', () => { mounted = true; return finishHydration(); });
    nuxtApp.hook('app:suspense:resolve', finishHydration);
    return { provide: { changeLanguage } };
  },
});
